import { useNode } from "@craftjs/core";
import { cn } from "@/lib/utils";
import { applyStyleProps, defaultStyleProps, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { VideoBlockSettings } from "@/components/craft/settings/VideoBlockSettings";

type VideoBlockProps = {
  url: string;
  aspectRatio?: "16/9" | "4/3";
  style?: ResponsiveStyleProps;
};

function embedUrl(url: string): string | null {
  if (!url.trim()) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  if (url.includes("embed")) return url;
  return null;
}

export function VideoBlock({ url, aspectRatio = "16/9", style = defaultStyleProps() }: VideoBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    id,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id }));
  const { style: inlineStyle, className: styleClass } = applyStyleProps(style, "desktop", id);
  const src = embedUrl(url);

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={cn("my-6", `craft-node-${id}`, styleClass, selected && "outline outline-2 outline-dashed outline-primary/30")}
      style={inlineStyle}
    >
      <div className={cn("relative w-full overflow-hidden rounded-xl bg-muted", aspectRatio === "16/9" ? "aspect-video" : "aspect-[4/3]")}>
        {src ? (
          <iframe src={src} title="Video" className="absolute inset-0 h-full w-full border-0" allowFullScreen />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Add a YouTube or Vimeo URL</div>
        )}
      </div>
    </div>
  );
}

VideoBlock.craft = {
  displayName: "Video",
  props: { url: "", aspectRatio: "16/9", style: defaultStyleProps() },
  rules: { canDrag: () => true },
  related: { settings: VideoBlockSettings },
};
